import { useMemo } from "react";
import Supercluster, { type AnyProps, type PointFeature } from "supercluster";
import { type Bbox, longitudeSpan, normalizeBbox } from "@/lib/geo-bounds";

export interface UseSuperclusterOptions {
  radius?: number;
  maxZoom?: number;
}

const DEFAULT_OPTIONS: UseSuperclusterOptions = { radius: 60, maxZoom: 16 };

const BOUNDS_PAD_FACTOR = 0.5;

function padBounds(bounds: Bbox): Bbox {
  const [west, south, east, north] = normalizeBbox(bounds);
  const span = longitudeSpan(west, east);
  const lngPad = span * BOUNDS_PAD_FACTOR;
  const latPad = (north - south) * BOUNDS_PAD_FACTOR;
  return normalizeBbox([west - lngPad, south - latPad, west + span + lngPad, north + latPad]);
}

export function useSupercluster(
  points: GeoJSON.Feature<GeoJSON.Point>[],
  bounds: Bbox | null,
  zoom: number,
  options: UseSuperclusterOptions = DEFAULT_OPTIONS,
) {
  const zoomInt = Math.round(zoom);

  const index = useMemo(() => {
    const sc = new Supercluster({
      radius: options.radius ?? 60,
      maxZoom: options.maxZoom ?? 16,
    });
    sc.load(points as PointFeature<AnyProps>[]);
    return sc;
  }, [points, options.radius, options.maxZoom]);

  const clusters = useMemo(
    () => (bounds ? index.getClusters(padBounds(bounds), zoomInt) : []),
    [index, bounds, zoomInt],
  );

  return { clusters, supercluster: index };
}
